import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
} from '@mui/material';
import { format, addMonths, subMonths, startOfDay } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import GanttChart from '../../../components/gantt';
import StaffMember from '../../../components/common/StaffMember';
import TaskMoveConfirmationDialog from '../components/TaskMoveConfirmationDialog';

// Hooks
import { useTasks } from '../hooks/useTasks';
import { useTaskCategories } from '../hooks/useTaskCategories';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useTaskPermissions } from '../../auth/hooks/usePermissions';
import { unavailabilityAPI } from '../../tidsregistering/services/unavailability';

// Utils
import { getPriorityColor } from '../utils/taskStateUtils';

import PageHeader from '../../../components/common/PageHeader';
import GroupIcon from '@mui/icons-material/Group';

// Custom component for rendering staff members in Gantt sidebar
const StaffMemberGanttCell = ({ category }) => {
  const { t } = useTranslation(['tasks', 'common']);

  // Count only pending and in-progress tasks where start date is today or before
  const today = startOfDay(new Date());
  const pendingTaskCount = category.tasks.filter(task => {
    const isPendingOrInProgress = task.state === 'PENDING' || task.state === 'IN_PROGRESS';
    const isNotGhost = !task.isGhost;
    const hasStarted = task.startDate <= today;
    return isPendingOrInProgress && isNotGhost && hasStarted;
  }).length;

  // Handle unassigned category
  if (category.id === 'unassigned') {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.600' }}>
          {category.name}
        </Typography>
        <Typography variant="caption" sx={{ color: 'grey.500' }}>
          {t('tasks:team_calendar.pending_tasks', { count: pendingTaskCount })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        px: 2,
        py: 1,
      }}
    >
      <StaffMember
        staffMember={category.staffMember}
        avatarSize={32}
        imageSize="64x64"
        gap={1.5}
        nameVariant="subtitle2"
        nameSx={{ fontWeight: 600, color: 'grey.800' }}
        clickable={true}
      />
      <Typography variant="caption" sx={{ color: 'grey.500' }}>
        {t('tasks:team_calendar.pending_tasks', { count: pendingTaskCount })}
      </Typography>
    </Box>
  );
};

export default function TeamCalendarPage() {
  const navigate = useNavigate();
  const { showError } = useSnackbar();
  const { t } = useTranslation(['tasks']);
  const { canUpdate } = useTaskPermissions();

  // State
  const [currentDate] = useState(new Date());
  const [calendarTasks, setCalendarTasks] = useState([]);
  const [unavailabilityBlocks, setUnavailabilityBlocks] = useState([]);
  const [moveConfirmationOpen, setMoveConfirmationOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);

  // API hooks
  const {
    loading,
    error,
    clearError,
    getCalendarTasks,
  } = useTasks();

  const { fetchTaskCategories } = useTaskCategories();

  // Tracks the full span of dates fetched so far (grows as the chart scrolls),
  // so a refetch-everything call (e.g. after a move) re-covers wherever the
  // user actually is instead of snapping back to a fixed window around today.
  const fetchedRangeRef = useRef({
    start: subMonths(currentDate, 1),
    end: addMonths(currentDate, 3),
  });

  // Load calendar data with exact date range from chart
  const loadCalendarData = useCallback(async (startDate = null, endDate = null) => {
    try {
      const rangeStart = startDate || fetchedRangeRef.current.start;
      const rangeEnd = endDate || fetchedRangeRef.current.end;
      fetchedRangeRef.current = {
        start: rangeStart < fetchedRangeRef.current.start ? rangeStart : fetchedRangeRef.current.start,
        end: rangeEnd > fetchedRangeRef.current.end ? rangeEnd : fetchedRangeRef.current.end,
      };
      const startStr = format(rangeStart, 'yyyy-MM-dd');
      const endStr = format(rangeEnd, 'yyyy-MM-dd');

      const [result] = await Promise.all([
        getCalendarTasks({ start_date: startStr, end_date: endStr }),
        unavailabilityAPI.getDepartmentUnavailability(startStr, endStr)
          .then((res) => {
            const newBlocks = res.data?.blocks ?? [];
            setUnavailabilityBlocks(prev => {
              const blockMap = new Map(prev.map(b => [b.source_id, b]));
              newBlocks.forEach(b => blockMap.set(b.source_id, b));
              return Array.from(blockMap.values());
            });
          })
          .catch(() => {}),
      ]);

      if (result.success) {
        const newTasks = result.data?.data?.tasks || [];
        setCalendarTasks(prev => {
          const taskMap = new Map(prev.map(t => [t.task_id, t]));
          newTasks.forEach(t => taskMap.set(t.task_id, t));
          return Array.from(taskMap.values());
        });
      } else {
        showError(t('tasks:team_calendar.failed_to_load'));
      }
    } catch (error) {
      showError(t('tasks:team_calendar.failed_to_load'));
    }
  }, [getCalendarTasks, showError, t]);

  // Function to handle when chart needs data for specific date range
  const handleDateRangeChange = useCallback((startDate, endDate) => {
    loadCalendarData(startDate, endDate);
  }, [loadCalendarData]);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        loadCalendarData(),
        fetchTaskCategories({ active_only: true }),
      ]);
    };
    loadData();
  }, [loadCalendarData, fetchTaskCategories]);

  // Transform tasks data for Gantt chart - grouped by staff members
  const ganttData = useMemo(() => {
    if (!calendarTasks.length) {
      return { categories: [], baseDate: new Date() };
    }

    // Group tasks by staff member (primary assignee)
    const tasksByStaff = {};
    const unassignedTasks = [];

    calendarTasks.forEach(task => {
      const primaryAssignee = task.primary_assignee;
      const primaryAssigneeId = primaryAssignee?.staff_member_id;
      const primaryAssigneeName = primaryAssignee?.name;

      if (primaryAssigneeId && primaryAssigneeName) {
        if (!tasksByStaff[primaryAssigneeId]) {
          tasksByStaff[primaryAssigneeId] = {
            id: primaryAssigneeId,
            name: primaryAssigneeName,
            staffMember: primaryAssignee, // Store full staff member object for custom component
            tasks: []
          };
        }
        tasksByStaff[primaryAssigneeId].tasks.push(task);
      } else {
        // Handle unassigned tasks
        unassignedTasks.push(task);
      }
    });

    // Add unassigned category if there are unassigned tasks
    if (unassignedTasks.length > 0) {
      tasksByStaff['unassigned'] = {
        id: 'unassigned',
        name: t('tasks:team_calendar.unassigned'),
        tasks: unassignedTasks
      };
    }

    // Transform to Gantt format
    const ganttCategories = Object.values(tasksByStaff).map(staffGroup => {
      return {
        id: staffGroup.id,
        name: staffGroup.name,
        component: StaffMemberGanttCell, // Custom component for staff member display
        staffMember: staffGroup.staffMember, // Pass staff member data to custom component
        tasks: staffGroup.tasks.map(task => {
          const startDate = task.scheduled_date 
            ? new Date(task.scheduled_date) 
            : new Date(task.created_at);
          
          const endDate = task.due_date 
            ? new Date(task.due_date) 
            : new Date(startDate.getTime() + (7 * 24 * 60 * 60 * 1000));

          return {
            id: task.task_id,
            name: task.title,
            startDate: startDate,
            endDate: endDate,
            color: getPriorityColor(task.priority),
            priority: task.priority,
            state: task.state,
            assignee: task.primary_assignee?.name,
            secondaryAssignee: task.secondary_assignee?.name,
            category: task.category_name,
            description: task.description,
            is_recurring: task.next_task_id === null ? task.is_recurring : null,
            rrule: task.next_task_id === null ? task.rrule : null,
            originalTask: task,
          };
        })
      };
    }).filter(staffGroup => staffGroup.tasks.length > 0);

    // Sort staff groups by name, with "Unassigned" at the end
    ganttCategories.sort((a, b) => {
      if (a.id === 'unassigned') return 1;
      if (b.id === 'unassigned') return -1;
      return a.name.localeCompare(b.name);
    });

    console.log('[TeamCalendar] category IDs:', ganttCategories.map(c => c));
    console.log('[TeamCalendar] unavailabilityBlocks:', unavailabilityBlocks);
    return {
      categories: ganttCategories,
      baseDate: currentDate,
    };
  }, [calendarTasks, currentDate, t, unavailabilityBlocks]);

  const handleTaskClick = useCallback((task) => {
    const taskId = task.task_id || task.originalTask?.task_id;
    if (taskId) {
      navigate(`/tasks/${taskId}`);
    }
  }, [navigate]);

  const handleTaskMove = useCallback(({ task, fromCategory, toCategory }) => {
    // Store the pending move and open confirmation dialog
    setPendingMove({ task, fromCategory, toCategory });
    setMoveConfirmationOpen(true);
  }, []);

  const handleMoveSuccess = useCallback(async ({ detachedFromRecurrence = false } = {}) => {
    setMoveConfirmationOpen(false);

    // Optimistically reflect the new assignee locally so the bar moves
    // immediately, instead of waiting on (and trusting) the refetch below.
    // When "this task only" detaches the task from its recurrence, also
    // clear rrule/is_recurring locally so its ghost instances don't follow
    // it to the new assignee's row before the refetch corrects it.
    if (pendingMove) {
      const { task, toCategory } = pendingMove;
      const taskId = task.id || task.originalTask?.task_id;
      setCalendarTasks(prev => prev.map(t => (
        t.task_id === taskId
          ? {
              ...t,
              primary_assignee: toCategory.id === 'unassigned' ? null : toCategory.staffMember,
              ...(detachedFromRecurrence && { is_recurring: false, rrule: null }),
            }
          : t
      )));
    }
    setPendingMove(null);

    // Reload calendar data after successful move to stay in sync with the server.
    // Small delay as a defensive buffer; the optimistic patch above is the
    // actual fix for instant feedback.
    await new Promise(resolve => setTimeout(resolve, 400));
    await loadCalendarData();
  }, [pendingMove, loadCalendarData]);

  const handleCloseMoveConfirmation = useCallback(() => {
    setMoveConfirmationOpen(false);
    setPendingMove(null);
  }, []);

  return (
    <Box>
      <PageHeader
        icon={GroupIcon}
        title={t('tasks:team_calendar.title')}
        subtitle={t('tasks:team_calendar.subtitle')}
      />
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Gantt Chart */}
      <Box sx={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}>
        {ganttData.categories.length > 0 ? (
          <GanttChart
            showHeader={false}
            categories={ganttData.categories}
            baseDate={ganttData.baseDate}
            onTaskClick={handleTaskClick}
            onDateRangeChange={handleDateRangeChange}
            onTaskMove={handleTaskMove}
            enableDnd={canUpdate}
            unavailabilityBlocks={unavailabilityBlocks}
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            <Typography variant="h6" color="text.secondary">
              {loading ? t('tasks:team_calendar.loading') : t('tasks:team_calendar.no_tasks')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Task Move Confirmation Dialog */}
      <TaskMoveConfirmationDialog
        open={moveConfirmationOpen}
        task={pendingMove?.task}
        fromCategory={pendingMove?.fromCategory}
        toCategory={pendingMove?.toCategory}
        onSuccess={handleMoveSuccess}
        onClose={handleCloseMoveConfirmation}
      />
    </Box>
  );
}